import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Cpu, Globe2, Layers, Route, Server, Sparkles } from 'lucide-react';
import { useLocationPath } from '../lib/router';
import { Markdown } from './Markdown';
import {
  alternativeBySlug,
  alternativeCategoryLabels,
  alternatives,
  type Alternative,
  type AlternativeCategory,
} from './alternativesData';

const categoryOrder: AlternativeCategory[] = ['cloud-hosting', 'model-infra', 'agent-cloud'];

const categoryIcons: Record<AlternativeCategory, React.ReactNode> = {
  'cloud-hosting': <Server className="h-4 w-4" />,
  'model-infra': <Cpu className="h-4 w-4" />,
  'agent-cloud': <Sparkles className="h-4 w-4" />,
};

const comparisonHighlights = [
  {
    icon: <Globe2 className="h-5 w-5" />,
    title: 'Hosts the app',
    copy: 'Deploy static sites, server containers, domains, logs, queues, secrets, and private builds.',
  },
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: 'Runs the agents',
    copy: 'Cloud coding agents work on repos, create durable artifacts, and hand off into deploy workflows.',
  },
  {
    icon: <Route className="h-5 w-5" />,
    title: 'Routes the models',
    copy: 'One OpenAI-compatible gateway covers text, image, video, audio, embeddings, search, and 3D generation.',
  },
  {
    icon: <Layers className="h-5 w-5" />,
    title: 'Owns GPU paths',
    copy: 'Cog and Comfy workloads scale from zero to GPU capacity, then sleep again after the burst.',
  },
];

function groupedAlternatives() {
  return categoryOrder.map((category) => ({
    category,
    label: alternativeCategoryLabels[category],
    items: alternatives.filter((alt) => alt.category === category),
  }));
}

const AlternativeDetail: React.FC<{ alt: Alternative }> = ({ alt }) => {
  React.useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, [alt.slug]);

  const related = alternatives.filter((item) => item.category === alt.category && item.slug !== alt.slug).slice(0, 4);

  return (
    <main className="px-4 pb-24 pt-28 sm:px-6">
      <article className="mx-auto max-w-3xl">
        <a href="/alternatives" className="inline-flex items-center gap-1.5 text-sm font-medium text-white/45 hover:text-white">
          <ArrowLeft size={15} /> All alternatives
        </a>

        <header className="mb-2 mt-4">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-white/40">
            <span className="inline-flex items-center gap-1.5 rounded border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
              {categoryIcons[alt.category]}
              {alt.categoryLabel}
            </span>
            <a href={alt.url} className="text-white/40 underline decoration-white/15 underline-offset-4 hover:text-white/70">
              {alt.competitor}
            </a>
            <span>{alt.readTime}</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{alt.title}</h1>
          <p className="mt-3 text-lg leading-7 text-white/60">{alt.excerpt}</p>
        </header>

        <Markdown source={alt.body} className="mt-6" />

        {related.length > 0 && (
          <section className="mt-14 border-t border-white/10 pt-7">
            <div className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-white/40">Related comparisons</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {related.map((item) => (
                <a
                  key={item.slug}
                  href={`/alternatives/${item.slug}`}
                  className="group rounded-lg border border-white/10 bg-white/[0.02] p-4 transition-colors hover:border-white/24 hover:bg-white/[0.045]"
                >
                  <div className="text-sm font-semibold text-white/80 group-hover:text-white">{item.competitor}</div>
                  <div className="mt-1 text-xs leading-5 text-white/42">{item.summary}</div>
                </a>
              ))}
            </div>
          </section>
        )}

        <footer className="mt-10 border-t border-white/10 pt-6">
          <a href="/alternatives" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-400 hover:underline">
            <ArrowLeft size={15} /> All alternatives
          </a>
        </footer>
      </article>
    </main>
  );
};

const AlternativesIndex: React.FC = () => {
  const groups = groupedAlternatives();

  return (
    <main className="mx-auto max-w-7xl px-6 pb-24 pt-28">
      <section className="mb-12 max-w-4xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xs uppercase tracking-[0.18em] text-white/45">
          Alternatives
        </div>
        <h1 className="text-4xl font-semibold tracking-tight md:text-6xl">
          Compare app.nz with cloud hosts and AI agent clouds.
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white/58">
          Fifty comparison guides for hosting platforms, model infrastructure providers, and AI coding-agent products. Most of these tools are good at one thing. app.nz puts agents, deploys, model routing, GPU endpoints, and app hosting in one place.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/signup" className="inline-flex items-center gap-2 rounded bg-sky-400 px-4 py-2 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300">
            Start building <ArrowRight className="h-4 w-4" />
          </a>
          <a href="/blog" className="inline-flex items-center gap-2 rounded border border-white/12 px-4 py-2 font-mono text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white">
            Engineering blog
          </a>
          <a href="/blog/what-is-an-ai-agent-cloud" className="inline-flex items-center gap-2 rounded border border-white/12 px-4 py-2 font-mono text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white">
            Why an agent cloud
          </a>
        </div>
      </section>

      <section className="mb-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {comparisonHighlights.map((item) => (
          <div key={item.title} className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded border border-white/10 bg-white/[0.04] text-white/70">
              {item.icon}
            </div>
            <h2 className="mb-2 text-lg font-semibold tracking-tight">{item.title}</h2>
            <p className="text-sm leading-relaxed text-white/48">{item.copy}</p>
          </div>
        ))}
      </section>

      <section className="mb-14 overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]">
        <div className="grid border-b border-white/10 md:grid-cols-3">
          {groups.map((group) => (
            <a
              key={group.category}
              href={`#${group.category}`}
              className="flex items-center justify-between gap-4 border-b border-white/10 p-5 transition-colors hover:bg-white/[0.035] md:border-b-0 md:border-r last:md:border-r-0"
            >
              <span>
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-white/40">
                  {categoryIcons[group.category]}
                  {group.label}
                </span>
                <span className="mt-2 block text-2xl font-semibold text-white">{group.items.length}</span>
              </span>
              <ArrowRight className="h-4 w-4 text-white/35" />
            </a>
          ))}
        </div>
        <div className="grid gap-0 md:grid-cols-3">
          {['Respect the specialist', 'Explain the tradeoff', 'Prefer truthful scope', 'Link to competitors', 'Show app.nz fit', 'Avoid fake benchmark claims'].map((feature) => (
            <div key={feature} className="flex items-center gap-3 border-b border-white/8 px-5 py-4 text-sm text-white/58 md:border-r">
              <CheckCircle2 className="h-4 w-4 flex-none text-emerald-300" />
              {feature}
            </div>
          ))}
        </div>
      </section>

      {groups.map((group) => (
        <section key={group.category} id={group.category} className="mb-16 scroll-mt-24">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-white/40">
                {categoryIcons[group.category]}
                {group.label}
              </div>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">{group.items.length} alternatives</h2>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {group.items.map((alt) => (
              <a
                key={alt.slug}
                href={`/alternatives/${alt.slug}`}
                className="group block h-full rounded-lg border border-white/10 bg-white/[0.02] p-6 transition-all hover:border-white/24 hover:bg-white/[0.045]"
              >
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="rounded border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
                    {alt.categoryLabel}
                  </span>
                  <span className="font-mono text-xs text-white/28">{alt.readTime}</span>
                </div>
                <h3 className="mb-3 text-xl font-semibold tracking-tight transition-colors group-hover:text-white">
                  {alt.title}
                </h3>
                <p className="mb-6 text-sm leading-relaxed text-white/50">
                  {alt.excerpt}
                </p>
                <div className="flex items-center gap-2 font-mono text-sm text-white/40 transition-colors group-hover:text-white">
                  Read comparison <ArrowRight className="h-4 w-4" />
                </div>
              </a>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
};

const AlternativesPage: React.FC<{ path?: string }> = ({ path: pathProp }) => {
  const path = useLocationPath(pathProp);
  const slug = React.useMemo(() => {
    const m = path.match(/^\/alternatives\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }, [path]);

  const alt = slug ? alternativeBySlug[slug] : undefined;

  if (slug && alt) return <AlternativeDetail alt={alt} />;

  if (slug && !alt) {
    return (
      <main className="px-4 pb-24 pt-28">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-2xl font-bold text-white">Alternative not found</h1>
          <a href="/alternatives" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:underline">
            <ArrowLeft size={15} /> All alternatives
          </a>
        </div>
      </main>
    );
  }

  return <AlternativesIndex />;
};

export default AlternativesPage;
